import {useQuery, UseQueryResult} from '@tanstack/react-query';
import {z as r} from 'zod';

import {ReviewsFilterFieldNameEnum} from '../../page/main/reviews/review-list-page-content/reviews-filter/reviews-filter-type';
import {ReviewsStateType} from '../../page/main/reviews/reviews-state/reviews-state-type';
import {fetchAndDeserialize, getUrlParameters} from '../../util/api-adapter';

const reviewAppSchema = r.object({
    id: r.string(),
    name: r.string(),
    catalogId: r.number(),
});

const reviewsAppListSchema = r.object({
    results: r.array(reviewAppSchema),
});

export type ReviewAppType = r.infer<typeof reviewAppSchema>;
export type ReviewsAppListType = r.infer<typeof reviewsAppListSchema>;

function getStoreCatalogIdsForAppsRequest(
    sourceList: Array<number>,
    storeCatalogIds: Array<number>
): Array<number> | null {
    if (sourceList.length === 0) {
        return null;
    }

    const selectedStoreCatalogIds = sourceList.filter((catalogId) => storeCatalogIds.includes(catalogId));

    return selectedStoreCatalogIds.length > 0 ? selectedStoreCatalogIds : null;
}

function getReviewsAppsUrl(reviewsState: ReviewsStateType, storeCatalogIds: Array<number>): string {
    const {filterState, filterId, company_ids: companyIds} = reviewsState;
    const brandIdsRaw = filterState[ReviewsFilterFieldNameEnum.brandIds];
    const brandIds = brandIdsRaw ? String(brandIdsRaw) : '';
    const selectedStoreCatalogIds = getStoreCatalogIdsForAppsRequest(
        filterState[ReviewsFilterFieldNameEnum.sourceList],
        storeCatalogIds
    );

    return `/v4/reviews/apps/${getUrlParameters({
        ...(!companyIds ? {filter_key: filterId} : null),
        ...(companyIds ? {company_ids: companyIds} : null),
        ...(brandIds ? {brand_ids: brandIds} : null),
        ...(selectedStoreCatalogIds ? {catalog_ids: selectedStoreCatalogIds.join(',')} : null),
    })}`;
}

async function fetchReviewsAppList(
    reviewsState: ReviewsStateType,
    storeCatalogIds: Array<number>
): Promise<ReviewsAppListType> {
    const url = getReviewsAppsUrl(reviewsState, storeCatalogIds);

    return fetchAndDeserialize<ReviewsAppListType>(url, reviewsAppListSchema);
}

export function useReviewsAppList(
    reviewsState: ReviewsStateType,
    storeCatalogIds: Array<number>,
    enabled: boolean
): UseQueryResult<ReviewsAppListType> {
    const url = getReviewsAppsUrl(reviewsState, storeCatalogIds);

    return useQuery([url], () => fetchReviewsAppList(reviewsState, storeCatalogIds), {
        enabled: enabled && storeCatalogIds.length > 0,
        retry: false,
        refetchOnMount: false,
        keepPreviousData: true,
        refetchOnWindowFocus: false,
        retryOnMount: false,
    });
}
